import React, { useState, useEffect } from 'react';
import { X, Search, Users, User } from 'lucide-react';
import { getResources, Resource } from '../../lib/resources';
import { useBusiness } from '../../contexts/BusinessContext';

interface SelectResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (resource: any) => void;
  selectedResources?: any[];
  selectedResourceId?: string | null;
}

export default function SelectResourceModal({
  isOpen,
  onClose,
  onSelect,
  selectedResources = [],
  selectedResourceId = null
}: SelectResourceModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadResources = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getResources(selectedBusiness.id);
      // Filter to only show active resources
      setResources(data.filter(resource => resource.status === 'active'));
      setError(null);
    } catch (err) {
      console.error('Error loading resources:', err);
      setError(err instanceof Error ? err.message : 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadResources();
    }
  }, [isOpen, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
    }
  };

  const filteredResources = resources.filter(resource => {
    const searchLower = searchQuery.toLowerCase();
    return (
      `${resource.first_name} ${resource.last_name}`.toLowerCase().includes(searchLower) ||
      resource.email?.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[999999]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Assign Resource</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Unassign Option */}
            <button
              onClick={() => {
                onSelect({ id: 'unassigned' });
                onClose();
              }}
              className="w-full flex items-center p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 mb-4"
            >
              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <div className="ml-3 text-left flex-1">
                <div className="text-sm font-medium text-gray-900">
                  Unassigned
                </div>
                <div className="text-sm text-gray-500">
                  Remove resource assignment
                </div>
              </div>
            </button>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading resources...</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-6">
                <div className="text-red-600 mb-2">{error}</div>
                <button
                  onClick={loadResources}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Try again
                </button>
              </div>
            ) : filteredResources.length === 0 ? (
              <div className="text-center py-6">
                <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">
                  {searchQuery ? 'No matching resources' : 'No resources available'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {searchQuery ? 'Try adjusting your search terms' : 'Add resources to get started'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredResources.map((resource) => {
                  const isSelected = selectedResources?.some(r => r.id === resource.id) || resource.id === selectedResourceId;
                  return (
                    <button
                      key={resource.id}
                      onClick={() => {
                        onSelect(resource);
                        onClose();
                      }}
                      disabled={isSelected}
                      className={`w-full flex items-center p-4 rounded-lg border relative ${
                        isSelected
                          ? 'border-gray-900 bg-gray-50 opacity-50 cursor-not-allowed'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-sm font-medium text-white overflow-hidden">
                        {resource.avatar_url ? (
                          <img 
                            src={resource.avatar_url} 
                            alt={`${resource.first_name} ${resource.last_name}`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          `${resource.first_name[0]}${resource.last_name[0]}`
                        )}
                      </div>
                      <div className="ml-3 text-left flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {resource.first_name} {resource.last_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {resource.email}
                        </div>
                      </div>
                      {isSelected && (
                        <span className="text-xs font-medium text-red-600">
                          Already assigned to this booking
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}